import { JsonRpcApiProvider, JsonRpcProvider, JsonRpcSigner } from 'ethers';

import { OnChainToken } from '#/config/configType';
import { getCurrentStateChainConfig } from '#/config/configUtils';

type Chain = {
  chainRpc: string;
  chainId: number;
  chainNetwork: string;
};

const anonymousProviderByChainCache = new Map<string, JsonRpcProvider>();

const getAnonymousWalletProviderForChain = (chain: Chain) => {
  const cacheKey = `${chain.chainId}:${chain.chainRpc}`;
  const cachedProvider = anonymousProviderByChainCache.get(cacheKey);
  if (cachedProvider) {
    return cachedProvider;
  }
  // const fetchRequest = new FetchRequest(chain.chainRpc + '/xz');
  // On 429 responses ethers by default retries up to 12x with ~250ms slots,
  // which hammers a rate-limited RPC even more. Use fewer attempts with
  // exponential backoff windows of up to ~2s/4s/8s instead.
  // fetchRequest.setThrottleParams({ slotInterval: 1000, maxAttempts: 5 });
  // fetchRequest.retryFunc = async (req, resp, attempt) => attempt < 2; // veto per-attempt
  const provider = new JsonRpcProvider(
    chain.chainRpc,
    {
      chainId: chain.chainId,
      name: chain.chainNetwork,
    },
    // chainId is known from config: skip eth_chainId network detection, whose
    // failure (e.g. on 429) makes ethers retry it in an endless 1s loop
    { staticNetwork: true }
  );
  anonymousProviderByChainCache.set(cacheKey, provider);
  return provider;
};

/** Anonymous wallet provider without knowledge of user wallet (no caller) */
export const getAnonymousRPCProvider = (
  overrideChain?: Chain
): JsonRpcProvider => {
  return getAnonymousWalletProviderForChain(
    overrideChain ?? getCurrentStateChainConfig()
  );
};

/** For many actions it's required for RPC provider to have Wallet available (caller wallet) */
export const getUncheckedWalletRPCProvider = (
  walletAddress: string,
  overrideChain?: Chain
): JsonRpcSigner => {
  const chain = getCurrentStateChainConfig();
  return new JsonRpcSigner(
    getAnonymousWalletProviderForChain(overrideChain ?? chain),
    walletAddress
  );
};

// export const getAnonymousWalletProvider = (
//   overrideChain?: Chain
// ): JsonRpcProvider => {
//   const chain = getCurrentStateChainConfig();
//   return getAnonymousWalletProviderForChain(overrideChain ?? chain);
// };

/**
 * Watch asset in Software Wallet - will trigger popup
 * To watch USDC use `USDCTokenOnKatanaEVM` as parameter.
 */
export const watchAssetInSoftwareWallet = (
  provider: JsonRpcProvider | JsonRpcApiProvider,
  token: OnChainToken
) => {
  return provider.send('wallet_watchAsset', {
    type: 'ERC20',
    options: {
      address: token.contractAddress,
      symbol: token.symbol,
      decimals: token.assetDecimals,
      // image: tokenImage
    },
  });
};
